import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSignUpApi } from '../../../api/ApiSignUp/ApiSignUp';

interface SignUpModalProps {
  showSubtext?: boolean;
}

const SignUpModal: React.FC<SignUpModalProps> = ({ showSubtext = true }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isHuman, setIsHuman] = useState(false);
  const [step, setStep] = useState<'form' | 'verify' | 'confirmed'>('form');
  const [canSubmit, setCanSubmit] = useState(false);
  const [codeValue, setCodeValue] = useState('');
  const [resentAt, setResentAt] = useState<number | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  const { signUp, verify, resend } = useSignUpApi();

  const emailLooksValid = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

  const recomputeCanSubmit = () => {
    const email = emailRef.current?.value?.trim() || '';
    const password = passwordRef.current?.value || '';
    const confirm = confirmRef.current?.value || '';
    // Enable button early when fields are filled and checkboxes are checked
    setCanSubmit(Boolean(email && password && confirm && acceptedTerms && isHuman && step === 'form'));
  };

  const isSandbox = typeof window !== 'undefined' && window.location.hostname.includes('sandbox');
  const termsHost = isSandbox ? 'sandbox.voycalc.com' : 'voycalc.com';
  const termsUrl = `${window.location.protocol}//${termsHost}/?page=terms`;

  const getAppUrl = () => {
    const current = new URL(window.location.href);
    const host = current.hostname.startsWith('app.') ? current.hostname : `app.${current.hostname}`;
    return `${current.protocol}//${host}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const email = emailRef.current?.value?.trim() || '';
    const password = passwordRef.current?.value || '';
    const confirm = confirmRef.current?.value || '';

    // Early checks
    if (!acceptedTerms) {
      setErrorMsg('You must accept the Terms to continue.');
      return;
    }
    if (!isHuman) {
      setErrorMsg("Please confirm you're not a robot.");
      return;
    }

    if (!email) {
      setErrorMsg('Please enter your email address.');
      return;
    }
    if (!emailLooksValid(email)) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    if (!password || !confirm) {
      setErrorMsg('Please enter your password in both fields.');
      return;
    }
    if (password.length < 8) {
      setErrorMsg('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await signUp({ email, password, plan: 'pro' });
      if (res.ok) {
        setSuccessMsg("Account created. We've sent a verification code to your email. Please check your inbox and spam/junk folder.");
        setRegisteredEmail(email);
        setStep('verify');
        setTimeout(() => codeRef.current?.focus(), 0);
        // keep modal open for code entry
      } else {
        const status = (res as any)?.data?.status;
        if (res.status === 409) {
          if (status === 'UNCONFIRMED') {
            // Existing user not yet verified → switch to verify step and resend automatically
            setRegisteredEmail(email);
            setStep('verify');
            setSuccessMsg(null);
            setTimeout(() => codeRef.current?.focus(), 0);
            try {
              const resendRes = await resend({ email });
              if (resendRes.ok) {
                setResentAt(Date.now());
                setCooldown(45);
                setErrorMsg(null);
              } else {
                setResendError(resendRes.message || `Resend failed (status ${resendRes.status})`);
              }
            } catch (e) {
              setResendError('Network error – please try again.');
            }
          } else if (status === 'CONFIRMED') {
            // User exists and is already verified → show Sign In option
            setRegisteredEmail(email);
            setSuccessMsg(null);
            setErrorMsg(null);
            setStep('confirmed');
          } else {
            // Pass-through for any other 409 (e.g., UNKNOWN)
            setErrorMsg(res.message || 'User already exists.');
          }
        } else {
          // Non-409 errors: generic pass-through
          setErrorMsg(res.message || `Request failed with status ${res.status}`);
        }
      }
    } catch (err) {
      console.error('Sign-up failed:', err);
      setErrorMsg('Network error – please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const email = (registeredEmail || emailRef.current?.value || '').trim();
    const code = (codeRef.current?.value || '').trim();
    if (!email) {
      setErrorMsg('Missing email.');
      return;
    }
    if (!/^\d+$/.test(code)) {
      setErrorMsg('Please enter a valid numeric verification code.');
      return;
    }

    setLoading(true);
    try {
      const res = await verify({ email, code });
      if (res.ok) {
        // redirect to app.<current-host>
        const current = new URL(window.location.href);
        const host = current.hostname.startsWith('app.') ? current.hostname : `app.${current.hostname}`;
        const target = `${current.protocol}//${host}`;
        window.location.assign(target);
      } else {
        setErrorMsg(res.message || `Verification failed (status ${res.status})`);
      }
    } catch (err) {
      console.error('Verify failed:', err);
      setErrorMsg('Network error – please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setErrorMsg(null);
    setResendError(null);
    setResentAt(null);
    const email = (registeredEmail || emailRef.current?.value || '').trim();
    if (!email) {
      setResendError('Missing email.');
      return;
    }
    setLoading(true);
    try {
      const res = await resend({ email });
      if (res.ok) {
        setResentAt(Date.now());
        setCooldown(45);
      } else {
        setResendError(res.message || `Resend failed (status ${res.status})`);
      }
    } catch (err) {
      console.error('Resend failed:', err);
      setResendError('Network error – please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal && emailRef.current) {
      emailRef.current.focus();
    }
  }, [showModal]);

  useEffect(() => {
    if (showModal && step === 'verify' && codeRef.current) {
      codeRef.current.focus();
    }
  }, [showModal, step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    recomputeCanSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedTerms, isHuman, step]);

  return (
    <>
      <button
        className="btn-hero-signup"
        onClick={() => { setErrorMsg(null); setSuccessMsg(null); setStep('form'); setShowModal(true); }}
      >
        Get Started
        <i className="bi bi-arrow-right ms-2"></i>
      </button>
      {showSubtext && (
        <p className="signup-subtext" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <br />We&apos;re currently in <strong>beta</strong> and participation is free!
          <br />
          <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="terms-link">Terms & Conditions</a>
        </p>
      )}

      {showModal && createPortal(
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            <h2>Create your account</h2>
            {step === 'form' && errorMsg && (
              <div className="alert alert-danger" role="alert">{errorMsg}</div>
            )}
            {step === 'form' && successMsg && (
              <div className="alert alert-success" role="alert">{successMsg}</div>
            )}
            {step === 'form' && (
              <p className="mb-3 small modal-subtext">
                Beta notice: This is pre-release software. We do not guarantee availability or correctness. Use at your own risk. After creating your account, you'll receive a verification code by email to activate it.
              </p>
            )}
            <form className="modal-form" onSubmit={step === 'form' ? handleSubmit : handleVerify}>
              {step === 'form' && (
                <div className="modal-form-group">
                  <label htmlFor="email" className="modal-form-label text-start">Email:</label>
                  <input ref={emailRef} type="email" id="email" name="email" className="modal-form-input" required onChange={recomputeCanSubmit} />
                </div>
              )}

              {step === 'form' && (
                <div className="modal-form-group">
                  <label htmlFor="password" className="modal-form-label text-start">Password (min 8 char):</label>
                  <input ref={passwordRef} type="password" id="password" name="password" className="modal-form-input" required onChange={recomputeCanSubmit} />
                </div>
              )}

              {step === 'form' && (
                <div className="modal-form-group">
                  <label htmlFor="confirm" className="modal-form-label text-start">Confirm password:</label>
                  <input ref={confirmRef} type="password" id="confirm" name="confirm" className="modal-form-input" required onChange={recomputeCanSubmit} />
                </div>
              )}

              {step === 'form' && (
                <div className="modal-form-group">
                  <label className="modal-form-label text-start">Plan:</label>
                  <input type="text" value="Pro (BETA)" className="modal-form-input input-disabled" readOnly />
                </div>
              )}

              {step === 'verify' && (
                <>
                  <div className="alert alert-info" role="alert">
                    Enter the verification code we sent to your email. If you don't see it, please check your <strong>spam/junk</strong> folder.
                  </div>
                  {errorMsg && (
                    <div className="alert alert-danger" role="alert">{errorMsg}</div>
                  )}
                  <div className="modal-form-group">
                    <label htmlFor="code" className="modal-form-label text-start">Verification code:</label>
                    <input
                      ref={codeRef}
                      type="text"
                      id="code"
                      name="code"
                      className="modal-form-input"
                      required
                      inputMode="numeric"
                      onChange={(e) => setCodeValue((e.target.value || '').replace(/\D+/g, ''))}
                      value={codeValue}
                      placeholder="Enter code"
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleResend}
                      disabled={loading || cooldown > 0}
                    >
                      {cooldown > 0 ? `Resend (${cooldown}s)` : 'Resend code'}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading || !/^\d+$/.test(codeValue)}>Verify &amp; Continue</button>
                  </div>
          {resentAt && (
            <div className="mt-2 small text-success">Verification code resent. Please check your inbox and spam/junk folder.</div>
          )}
          {resendError && (
            <div className="mt-2 small text-danger">{resendError}</div>
          )}
                </>
              )}

              {step === 'confirmed' && (
                <>
                  <div className="alert alert-info" role="alert">
                    This email is already verified. Please sign in to continue.
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => window.location.assign(getAppUrl())}
                    >
                      Sign in
                    </button>
                  </div>
                </>
              )}

              {step === 'form' && (
                <div className="form-check mb-3 text-start">
                  <input className="form-check-input" type="checkbox" id="acceptTerms" checked={acceptedTerms} onChange={(e) => { setAcceptedTerms(e.target.checked); }} />
                  <label className="form-check-label" htmlFor="acceptTerms">
                    I have read and accept the <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="terms-link">Terms & Conditions</a>
                  </label>
                </div>
              )}

              {step === 'form' && (
                <div className="modal-form-group form-check text-start">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="robotCheck"
                    checked={isHuman}
                    onChange={(e) => { setIsHuman(e.target.checked); }}
                  />
                  <label className="form-check-label" htmlFor="robotCheck">
                    I'm not a robot
                  </label>
                </div>
              )}

              {step === 'form' && (
                <button type="submit" className="btn btn-primary" disabled={loading || !canSubmit}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    'Create account'
                  )}
                </button>
              )}
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default SignUpModal;